/**
 * Qubba — the domed chamber.
 *
 * A square room whose corners are corbelled away by squinches high above the
 * hang line, turning the plan into a regular octagon; an octagonal collar
 * flares up from that and carries a shallow hemispherical dome lit by a cove
 * of light at its springing and an oculus at the crown. At eye level it is
 * still four flat walls, so the pictures hang as they would anywhere else:
 * the biggest run takes a gallery-wall cluster, the rest take single icons.
 *
 * role: 'wing'.
 */

import { rectSegments } from '../hang';

// stone/water/etc. are contract materials; fall back if the kit predates them.
const mat = (kit, name, fallback) => kit.materials[name] || kit.materials[fallback];

const WALL_TOP = 5.2; // drum top = node.height; the dome rises above it
const SQUINCH_Y = 4.0; // springing of the corner chamfers
const COLLAR_H = 0.9; // octagonal attic between wall top and dome
const DOME_SQUASH = 0.85; // saucer dome rather than a true hemisphere
const OCT = Math.PI / 8;

const qubba = {
  id: 'qubba',
  role: 'wing',
  weight: 2,

  plan(rand) {
    // Square: the octagon and the dome only read if w === d.
    const side = 11 + rand() * 2;
    return { w: side, d: side, height: WALL_TOP, params: {} };
  },

  segments(node) {
    // Straight runs on the four flat walls. Longest gets the cluster; the
    // remaining runs each carry one hero piece, shrine-fashion.
    return rectSegments(node).map((s, i) => ({
      ...s,
      style: i === 0 ? 'cluster' : 'hero',
    }));
  },

  build(node, T) {
    const { WALL_T } = T.PALACE;
    const THREE = T.THREE;
    const cx = (node.x1 + node.x2) / 2;
    const cz = (node.z1 + node.z2) / 2;
    const a = (node.x2 - node.x1) / 2 - WALL_T; // octagon apothem = half interior
    const Rc = a / Math.cos(OCT); // circumradius of the wall-top octagon
    const collarTop = WALL_TOP + COLLAR_H;
    const domeR = 0.9 * Rc;

    const group = new THREE.Group();
    const colliders = [];

    // --- the four straight walls, doors cut ------------------------------
    const wallGeos = [];
    for (const side of ['N', 'S', 'E', 'W']) {
      const { geos, colliders: c } = T.wallForSide(node, side, WALL_TOP);
      wallGeos.push(...geos);
      colliders.push(...c);
    }
    group.add(T.mergedMesh(wallGeos, T.kit.wallFor(node.palette || 0)));

    group.add(T.mergedMesh(
      [T.box(node.x1, node.x2, -0.25, 0, node.z1, node.z2)],
      T.kit.materials.marble
    ));

    // --- squinches: corner chamfers that start well above head height ----
    // Their centrelines are pushed out along the diagonal so the chamfer's
    // inner face lands exactly on the octagon apothem, flush with the walls.
    const chamfer = a * (1 - 1 / (1 + Math.SQRT2)); // regular-octagon corner cut
    const push = (WALL_T / 2) / Math.SQRT2;
    const stoneGeos = [];
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        const ax = cx + sx * (a - chamfer) + sx * push;
        const az = cz + sz * a + sz * push;
        const bx = cx + sx * a + sx * push;
        const bz = cz + sz * (a - chamfer) + sz * push;
        // The chamfer is entirely above 4m: no collider (the square walls
        // behind it already stop the visitor).
        const { geo } = T.angledWall(ax, az, bx, bz, WALL_TOP, WALL_T, SQUINCH_Y);
        stoneGeos.push(geo);

        // Stepped corbel bracket underneath, so the squinch looks carried.
        const kx = cx + sx * a;
        const kz = cz + sz * a;
        for (let k = 0; k < 3; k++) {
          const f = 0.4 + k * 0.3;
          const y0 = SQUINCH_Y - 0.66 + k * 0.22;
          const q = T.angledWall(
            kx + (ax - kx) * f, kz + (az - kz) * f,
            kx + (bx - kx) * f, kz + (bz - kz) * f,
            y0 + 0.24, WALL_T * 0.7, y0
          );
          stoneGeos.push(q.geo);
        }
      }
    }
    group.add(T.mergedMesh(stoneGeos, mat(T.kit, 'stone', 'marble')));

    // --- collar + dome, one DoubleSide shell -----------------------------
    // The collar is an 8-sided flare: its bottom rim matches the wall-top
    // octagon exactly, its top rim is the circle the dome springs from, so
    // there is no seam to look through.
    const collar = new THREE.CylinderGeometry(domeR, Rc, COLLAR_H, 8, 1, true);
    collar.rotateY(OCT); // put the octagon's flats on the axes
    collar.translate(cx, WALL_TOP + COLLAR_H / 2, cz);

    const dome = new THREE.SphereGeometry(domeR, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2);
    dome.scale(1, DOME_SQUASH, 1);
    dome.translate(cx, collarTop, cz);
    group.add(T.mergedMesh([collar, dome], T.kit.materials.dome));

    // --- light: a cove ring at the springing, an oculus at the crown -----
    const apex = collarTop + DOME_SQUASH * domeR;
    const cove = new THREE.Mesh(
      // Kept inside the collar's apothem at this height so it never pokes
      // out through the octagon's flat faces.
      new THREE.RingGeometry(Rc * 0.76, Rc * 0.895, 32, 1),
      T.kit.materials.laylight
    );
    cove.rotation.x = Math.PI / 2;
    cove.position.set(cx, WALL_TOP + 0.14, cz);
    cove.matrixAutoUpdate = false;
    cove.updateMatrix();

    const oculus = new THREE.Mesh(
      new THREE.CircleGeometry(domeR * 0.2, 24),
      T.kit.materials.laylight
    );
    oculus.rotation.x = Math.PI / 2;
    oculus.position.set(cx, apex - 0.14, cz);
    oculus.matrixAutoUpdate = false;
    oculus.updateMatrix();
    group.add(cove, oculus);

    const light = new THREE.PointLight(0xfff1d8, 26, a * 3.4, 1.9);
    light.position.set(cx, WALL_TOP - 0.4, cz);
    group.add(light);

    // --- trim, casings, and an octagonal floor star ----------------------
    group.add(T.mergedMesh(T.baseAndCornice(node, WALL_TOP), T.kit.materials.trim));
    const casings = T.casingsForNode(node);
    if (casings.length) group.add(T.mergedMesh(casings, T.kit.materials.marble));

    const inlay = new THREE.CylinderGeometry(a * 0.42, a * 0.42, 0.09, 8, 1);
    inlay.rotateY(OCT);
    inlay.translate(cx, -0.025, cz); // sunk into the slab: no coplanar faces
    group.add(T.mergedMesh([inlay], mat(T.kit, 'stone', 'floor')));

    // No furniture: every door on a square plan can face the centre, so the
    // floor stays clear rather than risking a blocked threshold.
    return { group, colliders };
  },
};

export default qubba;
